import { appeler, EchecApi, poserJetonCsrf } from './api.js';

/**
 * LA SESSION, VUE PAR L'INTERFACE.
 *
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║ L'APPLICATION DEMANDE « QUI SUIS-JE ? » ET DECIDE QUOI AFFICHER.      ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 *
 * nginx ne sait RIEN de la session : il sert des fichiers statiques, les memes
 * pour tout le monde. C'est l'application qui, au chargement, appelle
 * `/auth/moi` et se resout :
 *
 *   200 -> session valide, on ouvre Anelya
 *   401 -> aucune session, on montre l'ecran de connexion
 *
 * Faire decider nginx aurait suppose qu'il lise le cookie, donc qu'il connaisse
 * le secret de session -- une seconde autorite d'authentification, qui
 * divergerait de la premiere au premier ajustement.
 *
 * ── LE JETON CSRF VIENT DES DEUX ROUTES, ET C'EST ESSENTIEL ─────────────
 *
 * `/auth/moi` le rend AUSSI, recalcule a chaque appel sans etat serveur. Ne le
 * poser qu'a la connexion aurait suffi jusqu'au premier RECHARGEMENT de page :
 * le jeton vit en memoire, la session vit dans un cookie. Apres F5, l'une
 * survit et l'autre non -- et toute mutation serait refusee par `csrf_refuse`
 * sans que l'utilisateur comprenne pourquoi.
 *
 * ── UN 401 N'EST PAS UNE ERREUR ─────────────────────────────────────────
 *
 * C'est la reponse NORMALE a « qui suis-je » quand personne n'est connecte.
 * La traiter comme un incident afficherait une alerte a chaque premiere visite.
 */

/** La forme rendue par `/auth/connexion` ET `/auth/moi`. */
export interface VueSession {
  readonly utilisateur: { readonly id: string; readonly email: string; readonly nom: string };
  readonly session: { readonly expire_le: string };
  readonly csrf_token: string;
  readonly locataires: readonly {
    readonly code: string; readonly nom: string; readonly role: string }[];
  readonly roles_plateforme: readonly string[];
}

export type EtatSession =
  | { readonly phase: 'chargement' }
  | { readonly phase: 'anonyme' }
  | { readonly phase: 'connecte'; readonly vue: VueSession }
  | { readonly phase: 'indisponible'; readonly message: string };

/** Pose le jeton et rend la vue : les deux chemins passent par ici. */
function accueillir(vue: VueSession): VueSession {
  poserJetonCsrf(vue.csrf_token);
  return vue;
}

export async function demanderQuiJeSuis(): Promise<EtatSession> {
  try {
    return { phase: 'connecte', vue: accueillir(await appeler<VueSession>('GET', '/auth/moi')) };
  } catch (e) {
    if (e instanceof EchecApi && e.detail.statut === 401) return { phase: 'anonyme' };
    // TOUT LE RESTE est un incident : API arretee, reponse illisible, panne
    // reseau. On le DIT plutot que de montrer un ecran de connexion trompeur --
    // l'utilisateur saisirait ses identifiants dans le vide.
    return { phase: 'indisponible',
      message: e instanceof EchecApi ? e.detail.message : 'Le service est injoignable.' };
  }
}

export async function seConnecter(email: string, motDePasse: string): Promise<VueSession> {
  // `/auth/connexion` rend DEJA la vue complete : aucun second appel n'est
  // necessaire, et en faire un doublerait le cout de chaque connexion.
  return accueillir(await appeler<VueSession>(
    'POST', '/auth/connexion', { email, mot_de_passe: motDePasse }));
}

export async function seDeconnecter(): Promise<void> {
  try { await appeler('POST', '/auth/deconnexion'); }
  finally {
    // MEME SI L'APPEL ECHOUE : l'utilisateur a demande a partir. Garder le
    // jeton en memoire apres un depart demande serait tenir une porte ouverte
    // qu'il croit fermee.
    poserJetonCsrf(undefined);
  }
}
